import { createContext, ReactNode, useContext, useMemo } from 'react';

import { csrClient } from '../apollo/api/avantArteClient';
import { storefrontClient } from '../apollo/shopify/storefrontClient';
import { usePreAuthDraw } from '../features/draws/hooks/usePreAuthDraw';
import { usePreAuthDrawIntent } from '../features/draws/hooks/usePreAuthDrawIntent';
import { PreAuthDrawController } from '../features/draws/types';
import AvantArteAccountGateway from '../gateways/AvantArteAccountGateway';
import AvantArteAuthGateway from '../gateways/AvantArteAuthGateway';
import { AvantArtePaymentGateway } from '../gateways/AvantArtePaymentGateway';

// TODO: look into deprecated AvantArteAuthGateway but also other gateways
const authGateway = new AvantArteAuthGateway(csrClient, storefrontClient);
const accountGateway = new AvantArteAccountGateway(csrClient);
const paymentGateway = new AvantArtePaymentGateway(csrClient);

interface PreAuthDrawContextProps {
  children: ReactNode;
}

const PreAuthDrawContext = createContext<PreAuthDrawController>({
  loading: () => false,
  updating: false,
  draw: undefined,
  bid: undefined,
  setBid: () => {},
  setupComplete: false,
  setupController: async () => {},
  setupSecret: undefined,
  entryIntentResponse: {
    intent: undefined,
    pricing: undefined,
    readyToConfirm: false,
    cryptoComPaymentId: undefined,
  },
  updateDrawEntryIntent: async () => {},
  errorMessage: undefined,
  setSelectedAddress: () => {},
  selectedAddress: '',
  addresses: [],
  refreshAddresses: async () => {},
  setSelectedPaymentMethodId: () => {},
  selectedPaymentMethodId: null,
  paymentMethods: [],
  selectedPaymentType: null,
  setSelectedPaymentType: () => {},
  paymentErrorCode: null,
  setPaymentErrorCode: () => {},
  usingNewPaymentMethod: false,
  newPaymentMethodType: '',
  setNewPaymentMethodType: () => {},
  newPaymentMethodUsingShippingAddress: true,
  setUsingNewPaymentMethod: () => {},
  setNewPaymentMethodUsingShippingAddress: () => {},
  billingAddress: null,
  setBillingAddress: () => {},
  setOptionalItems: () => {},
  optionalItems: [],
  setCustomerSelectedFrame: () => {},
  customerSelectedFrame: null,
  dispatchDate: { isPast: false, range: null, weeks: null },
  resetController: () => {},
  activeDraw: null,
  isPreOrderSelected: false,
  setIsPreOrderSelected: () => {},
});

export const PreAuthDrawProvider = ({ children }: PreAuthDrawContextProps) => {
  const state = usePreAuthDraw({
    accountGateway,
    authGateway,
    paymentGateway,
  });

  // Setup draw intent
  usePreAuthDrawIntent(state.draw?.id);

  const context = useMemo(() => state, [state]);

  return (
    <PreAuthDrawContext.Provider value={context}>
      {children}
    </PreAuthDrawContext.Provider>
  );
};

export const usePreAuthDrawCtx = () => useContext(PreAuthDrawContext);
